import { MoneyV2 } from '@/__generated__/storefront/graphql';

export const MONEY_FORMAT_PLACEHOLDERS = {
  AMOUNT: 'amount',
  AMOUNT_NO_DECIMALS: 'amount_no_decimals',
  AMOUNT_WITH_COMMA_SEPARATOR: 'amount_with_comma_separator',
  AMOUNT_NO_DECIMALS_WITH_COMMA_SEPARATOR:
    'amount_no_decimals_with_comma_separator',
  AMOUNT_WITH_APOSTROPHE_SEPARATOR: 'amount_with_apostrophe_separator'
} as const;

export const VALUE_NOT_REACHED_FALLBACK = '-';
const FALLBACK_MONEY_FORMAT = '${{amount}}';
const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/;
const THOUSAND_SEPARATOR_REGEX = /(\d)(?=(\d{3})+(?!\d))/g;

/**
 * Apply currency-specific rounding logic
 * if a currency code requires rounding, round the amount
 * thats essentially all currencies except EUR, GBP and USD
 * some context can be found here:
 * https://hobbii.atlassian.net/wiki/spaces/TECH/pages/2609250306/Price+Calculations
 * https://hobbii.atlassian.net/wiki/spaces/TECH/pages/3082354689/Price+Rounding+Pretty+Prices
 */
const applyCurrencyRounding = (cents: number): number => {
  const currency = window?.Shopify?.currency?.active;

  if (
    currency &&
    currency !== 'EUR' &&
    currency !== 'GBP' &&
    currency !== 'USD' &&
    currency !== 'CAD'
  ) {
    return Math.round(cents / 100) * 100;
  }

  return cents;
};

const applyCADFormatAdjustment = (format?: string): string | undefined => {
  const currency = window?.Shopify?.currency?.active;
  const enablePresentmentCADCurrency =
    window?.Hobbii?.flags?.enablePresentmentCADCurrency;

  if (
    enablePresentmentCADCurrency &&
    currency === 'CAD' &&
    format &&
    format.includes('$')
  ) {
    return format.replace('$', 'CA$ ');
  }

  return format;
};

const formattedPrice = (value: number) => {
  const format = window?.Hobbii?.moneyFormat;
  const roundedValue = applyCurrencyRounding(value);
  return formatMoney(roundedValue, format);
};

const formatPriceStorefront = (price?: MoneyV2) => {
  const cents = parseFloat(price?.amount || '0') * 100;
  const format = window?.Hobbii?.moneyFormat;
  const roundedCents = applyCurrencyRounding(cents);
  return formatMoney(roundedCents, format);
};

const formatMoney = (cents: number, format?: string): string => {
  if (typeof cents !== 'number' || Number.isNaN(cents)) {
    if (import.meta.env.DEV) {
      throw new Error(
        `formatMoney: cents argument not of accepted format ${JSON.stringify(
          cents
        )}, must be a number.`
      );
    }

    return VALUE_NOT_REACHED_FALLBACK;
  }

  const adjustedFormat = applyCADFormatAdjustment(format);

  const formatString = adjustedFormat ?? FALLBACK_MONEY_FORMAT;

  const targetPlaceholderMatch = formatString.match(PLACEHOLDER_REGEX);
  const targetPlaceholder = targetPlaceholderMatch?.[1];

  // NOTE: we could fall back to `Intl.NumberFormat` instead of defaulting to ${{amount}}':
  // new Intl.NumberFormat(window.Hobbii?.storeLocale, { style: 'currency', currency: window.Shopify.currency.active}).format(1000000)
  const formattedValue = targetPlaceholder
    ? formatWithPlaceholder(cents, targetPlaceholder)
    : formatWithDelimiters(cents);
  return formatString.replace(PLACEHOLDER_REGEX, formattedValue);
};

const formatWithPlaceholder = (
  cents: number,
  targetPlaceholder: string
): string => {
  switch (targetPlaceholder) {
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT:
      return formatWithDelimiters(cents);
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_NO_DECIMALS:
      return formatWithDelimiters(cents, 0);
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_WITH_COMMA_SEPARATOR:
      return formatWithDelimiters(cents, 2, '.', ',');
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_NO_DECIMALS_WITH_COMMA_SEPARATOR:
      return formatWithDelimiters(cents, 0, '.', ',');
    case MONEY_FORMAT_PLACEHOLDERS.AMOUNT_WITH_APOSTROPHE_SEPARATOR:
      return formatWithDelimiters(cents, 2, "'", '.');
  }

  if (import.meta.env.DEV) {
    throw new Error(
      `formatMoney: No match for placeholder "${targetPlaceholder}" found when formatting "${cents}" cents.`
    );
  }

  // NOTE: default to returning MONEY_FORMAT_PLACEHOLDERS.AMOUNT in prod.
  // This will default to "{{amount}} bananas" for stuff like "{{bananas}} bananas"
  return formatWithDelimiters(cents);
};

const formatWithDelimiters = (
  cents: number,
  precision = 2,
  thousands = ',',
  decimal = '.'
): string => {
  const numberStr = (Math.round(cents) / 100).toFixed(precision);
  const [wholePart, decimalPart] = numberStr.split('.');

  const wholePartFormatted = wholePart.replace(
    THOUSAND_SEPARATOR_REGEX,
    `$1${thousands}`
  );

  //
  const decimalPartFormatted =
    decimalPart && decimalPart !== '00' ? decimal + decimalPart : '';

  return wholePartFormatted + decimalPartFormatted;
};

export { formatPriceStorefront, formattedPrice };
